<template>
  <div class="gallery-wrapper">
    <div v-masonry item-selector=".item" transition-duration="0.5s" class="masonryWrap">
      <div v-for="(image, index) in images" :key="index" v-masonry-tile class="item">
        <img :src="image" alt="伊豆大島の写真" @click="openModal(image)" />
      </div>
    </div>
    <!-- modal -->
    <transition name="modal-fade" appear>
      <div class="modal" v-if="showModal" @click="closeModal">
        <img :src="modalImage" alt="Selected Izu Oshima image" />
      </div>
    </transition>
    <div v-if="isLoading" class="loading-indicator">
      <p>Loading...</p>
    </div>
  </div>
</template>

<script>
import axios from "axios";

export default {
  name: "Gallery",
  props: {
    folder: {
      type: String,
      default: "All", // デフォルトで全フォルダを対象
    },
    limit: {
      type: Number,
      default: 30
    },
    enablePagination: { // ページネーションのオンオフ
      type: Boolean,
      default: false, // デフォルトはオフ
    },
  },
  data() {
    return {
      images: [],
      showModal: false,
      modalImage: "",
      isLoading: true,
      nextContinuationToken: null, // ページングトークン
      isFinished: false, // 全データを取得済みか
    };
  },
  async created() {
    await this.fetchImages(); // コンポーネント作成時に画像を取得
    if (this.enablePagination) {
    window.addEventListener("scroll", this.handleScroll);
    }
  },
  methods: {
    async fetchImages() {
      if (this.isFinished) return; // 重複ロードを防止
      this.isLoading = true;

      try {
        const apiUrl = process.env.VUE_APP_API_URL_FETCH_S3_IMAGES;
        const folderPath = `gallery/${this.folder || ""}`;
        const queryParams = {
          folder: folderPath,
          limit: this.limit,
          continuationToken: this.nextContinuationToken
        };

        const queryString = new URLSearchParams(queryParams).toString();
        const url = `${apiUrl}?${queryString}`;

        const response = await axios.get(url);
        const parsedBody = JSON.parse(response.data.body);

        this.images.push(...parsedBody.images); // 新しいデータを追加
        this.nextContinuationToken = parsedBody.nextContinuationToken;

        if (!this.nextContinuationToken) {
          this.isFinished = true; // トークンがない場合、全データ取得済み
        }
      } catch (error) {
        console.error("Error fetching images:", error);
      } finally {
        this.isLoading = false;
      }
    },
    // スクロールイベントで追加データを取得
    handleScroll() {
      if (!this.enablePagination) return; // ページネーションがオフの場合は処理しない

      const scrollContainer = document.documentElement || document.body; // スクロール対象を明確化
      const scrollPosition = scrollContainer.scrollTop + window.innerHeight;
      const bottomPosition = scrollContainer.scrollHeight - 150; // ページの下端 - マージン

      if (scrollPosition >= bottomPosition && !this.isLoading && !this.isFinished) {
        this.fetchImages();
      }
    },
    openModal(image) {
      this.showModal = true;
      this.modalImage = image;
    },
    closeModal() {
      this.modalImage = "";
      this.showModal = false;
    },
  },
  mounted() {
    if (typeof this.$redrawVueMasonry === 'function') {
      this.$redrawVueMasonry();
    }
  },
  beforeDestroy() {
    if (this.enablePagination) {
    window.removeEventListener("scroll", this.handleScroll);
    } 
  },
};
</script>

<style scoped>
.gallery-wrapper {
  position: relative;
  height: auto;
  width: 100%;
}

.loading-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  height: auto;
  font-size: 1em;
  color: #666;
  opacity: 0.8;
  animation: blink 1s infinite;
  margin-top: 100px;
}

@keyframes blink {

  0%,
  100% {
    opacity: 0.8;
    /* 通常状態 */
  }

  50% {
    opacity: 0.5;
    /* 点滅状態 */
  }
}

.masonryWrap {
  position: relative;
  display: flex;
  justify-content: center;
  justify-self: center;
  margin: auto;
  width: 100%;
}

.item {
  width: calc(100% / 4 - 20px);
  /* タイル間の余白 */
  margin: 6px;
  cursor: pointer;
}

.item :hover {
  transform: scale(1.03);
  transition: transform 0.3s;
}

@media (hover: none) {
  .item :hover {
    transform: none;
    /* ホバー時の拡大を無効化 */
    transition: none;
    /* トランジションも無効化 */
  }
}

.item img {
  filter: sepia(20%);
  border-radius: 15px;
  display: block;
  width: 100%;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100dvh;
  background: rgba(0, 0, 0, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.modal img {
  z-index: 999;
  width: 60%;
  max-height: 80%;
  object-fit: contain;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-active {
  opacity: 0;
}

.modal-fade-enter-to {
  opacity: 1;
}

.modal-fade-leave {
  opacity: 1;
}

.modal-fade-leave-to {
  opacity: 0;
}


/* タブレット、スマートフォン向けのスタイル */
@media screen and (max-width: 1024px) {
  .item {
    width: calc(100% / 3 - 20px);
  }

  .modal img {
    width: 90%;
  }
}

@media screen and (max-width: 570px) {

  .item {
    width: calc(100% / 2 - 5px);
    margin: 2px;
  }

  .modal img {
    width: 90%;
    max-height: 80%;
  }
}
</style>